import { useEffect, useRef } from 'react';
import { useEmbedLayoutStore } from '@embed/embedLayoutStore';

interface EmbedScalerProps {
  /** Target element or element id to zoom (e.g., the wrapper with id "ppt-stage"). */
  target?: string | HTMLElement;
}

// Embed slide baselines. These are SMALLER than the slide's native authoring grid
// (`useScale`/`getDimensions`: rectangle 1920x1080) — a deliberate apparent-size
// choice (denser grid → bigger-looking text after `zoom`). Trade-off: fixed-px raster
// assets (the 720x360 bg img, icons) get mildly bitmap-scaled by `zoom`; text stays
// re-rasterized/crisp and is further enlarged via the root font bump below.
const BASELINES = {
  rectangle: { width: 1600, height: 900 },
  square: { width: 1080, height: 1080 },
} as const;

// Bump the root (html) font-size in embeds so rem-based slide text reads larger.
// rem resolves against the html element (browser default ~16px); the bump adds to
// that base, scaling every rem value proportionally. Applied only while an embed is
// mounted; restored on unmount. Tune FONT_BUMP_PX to taste.
const ROOT_FONT_BASE_PX = 16;
const FONT_BUMP_PX = 0.2;

// Zoom slightly past contain-fit so the slide fills more of the pane (edges crop
// via `overflow: hidden` on the stage and its centering parent). Keeps the native
// grid (crisp) while restoring the larger apparent size contain-fit alone loses.
const OVERSCAN = 1.01;

// Fits #ppt-stage to the viewport for embeds (PowerPoint add-in, Gamma, Canva)
// using CSS `zoom` instead of `transform: scale()`. Unlike a transform, `zoom`
// does not become the containing block for `position: fixed` descendants, so
// modals that portal to document.body escape the slide zoom entirely — they
// render at native scale and fit the pane via `.embed-modal-fit` (modal.tsx).
// The published `--embed-zoom` custom property remains for viewport-height
// compensation inside the stage (`.embed-zoom`).
// Does not wrap children; it mutates the target inline styles and restores them on
// unmount.
const EmbedScaler: React.FC<EmbedScalerProps> = ({ target = 'ppt-stage' }) => {
  const layoutMode = useEmbedLayoutStore((s) => s.layoutMode);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const originalTargetStylesRef = useRef<{
    zoom?: string;
    width?: string;
    height?: string;
    overflow?: string;
    className?: string;
  } | null>(null);
  const originalMeasureStylesRef = useRef<{
    backgroundColor?: string;
    display?: string;
    height?: string;
    placeItems?: string;
    justifyContent?: string;
    alignItems?: string;
    overflow?: string;
  } | null>(null);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const el: HTMLElement | null = target instanceof HTMLElement
      ? target
      : document.getElementById(target);
    if (!el) return;

    const measureEl: HTMLElement = el.parentElement || document.documentElement;

    // Enlarge rem-based slide text via the root font-size (see FONT_BUMP_PX).
    const originalRootFontSize = document.documentElement.style.fontSize;
    document.documentElement.style.fontSize = `${ROOT_FONT_BASE_PX + FONT_BUMP_PX}px`;

    if (!originalMeasureStylesRef.current) {
      originalMeasureStylesRef.current = {
        backgroundColor: measureEl.style.backgroundColor,
        display: measureEl.style.display,
        height: measureEl.style.height,
        placeItems: (measureEl.style as any).placeItems,
        justifyContent: measureEl.style.justifyContent,
        alignItems: measureEl.style.alignItems,
        overflow: measureEl.style.overflow,
      };
    }
    // Black letterbox around the scaled canvas, centered via CSS grid. `overflow:
    // hidden` clips the overscanned stage to the viewport instead of scrolling.
    if (measureEl.parentElement) measureEl.parentElement.style.backgroundColor = '#000000';
    measureEl.style.display = 'grid';
    measureEl.style.height = '100vh';
    (measureEl.style as any).placeItems = 'center';
    measureEl.style.overflow = 'hidden';
    if (!measureEl.style.justifyContent) measureEl.style.justifyContent = 'center';
    if (!measureEl.style.alignItems) measureEl.style.alignItems = 'center';

    if (!originalTargetStylesRef.current) {
      originalTargetStylesRef.current = {
        zoom: (el.style as any).zoom,
        width: el.style.width,
        height: el.style.height,
        overflow: el.style.overflow,
        className: el.className,
      };
    }

    const applyZoom = () => {
      const { width: baseW, height: baseH } = BASELINES[layoutMode] ?? BASELINES.rectangle;
      const viewportWidth = window.innerWidth || baseW;
      const viewportHeight = window.innerHeight || baseH;
      const zoom = Math.min(viewportWidth / baseW, viewportHeight / baseH) * OVERSCAN;

      (el.style as any).zoom = String(zoom);
      el.style.width = `${baseW}px`;
      el.style.height = `${baseH}px`;
      el.style.overflow = 'hidden';
      el.className = 'h-full bg-grey-50 dark:bg-grey-900 ppt-stage';

      // Publish for viewport-height compensation inside the zoomed stage (see
      // `.embed-zoom` in globals.css). Modals do NOT consume this — they render
      // at native scale and fit the pane via `.embed-modal-fit` (modal.tsx).
      document.documentElement.style.setProperty('--embed-zoom', String(zoom));
    };

    applyZoom();

    // ResizeObserver covers window resizes too.
    resizeObserverRef.current = new ResizeObserver(applyZoom);
    resizeObserverRef.current.observe(measureEl);
    resizeObserverRef.current.observe(document.documentElement);

    return () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;

      document.documentElement.style.fontSize = originalRootFontSize || '';

      if (originalTargetStylesRef.current) {
        (el.style as any).zoom = originalTargetStylesRef.current.zoom || '';
        el.style.width = originalTargetStylesRef.current.width || '';
        el.style.height = originalTargetStylesRef.current.height || '';
        el.style.overflow = originalTargetStylesRef.current.overflow || '';
        el.className = originalTargetStylesRef.current.className || '';
        originalTargetStylesRef.current = null;
      }

      if (originalMeasureStylesRef.current) {
        measureEl.style.backgroundColor = originalMeasureStylesRef.current.backgroundColor || '';
        measureEl.style.display = originalMeasureStylesRef.current.display || '';
        measureEl.style.height = originalMeasureStylesRef.current.height || '';
        (measureEl.style as any).placeItems = originalMeasureStylesRef.current.placeItems || '';
        measureEl.style.justifyContent = originalMeasureStylesRef.current.justifyContent || '';
        measureEl.style.alignItems = originalMeasureStylesRef.current.alignItems || '';
        measureEl.style.overflow = originalMeasureStylesRef.current.overflow || '';
        originalMeasureStylesRef.current = null;
      }

      document.documentElement.style.removeProperty('--embed-zoom');
    };
  }, [target, layoutMode]);

  return null;
};

export default EmbedScaler;
